/**
 * Role Definitions Service
 *
 * API client for managing org-scoped party role definitions.
 */

import type { ApiResponse } from './api-client'

const API_BASE = '/api/v1/organizations'

async function request<T>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...options?.headers },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as { message?: string; code?: string }
      return {
        error: {
          message: errorData.message ?? `Request failed: ${response.statusText}`,
          code: errorData.code ?? response.status.toString(),
        },
      }
    }

    if (response.status === 204) { return { data: undefined as unknown as T } }
    const data = await response.json() as T
    return { data }
  } catch (error) {
    return {
      error: {
        message: error instanceof Error ? error.message : 'Network request failed',
        code: 'NETWORK_ERROR',
      },
    }
  }
}

// ============================================================================
// Types
// ============================================================================

export interface AccountMapping {
  /** Accounting purpose: "receivable" | "payable" | "advance" | "vendor_advance" | "deposit" */
  purpose: string
  /** Explicit head-account code from the Chart of Accounts (e.g. "1200").
   *  Subledger code becomes "{parentAccountCode}-{partyCode}" e.g. "1200-C001".
   *  Takes priority over parentTag when set. */
  parentAccountCode: string
  /** Fallback system tag (used only if parentAccountCode is blank) */
  parentTag: string
  parentAccountType: string
  parentSubtype: string
  /** Human label for the mapping, e.g. "Accounts Receivable" */
  description: string
  /** Suffix appended to the subledger name, e.g. "Receivable" */
  ledgerSuffix: string
}

export interface BillingConfig {
  canReceiveInvoices: boolean
  canIssueInvoices: boolean
  invoiceTypes: string[]
  paymentDirection: string
}

export interface RoleDefinition {
  id: string
  orgId: string
  role: string
  label: string
  description: string
  accountMappings: AccountMapping[]
  billing: BillingConfig
  isActive: boolean
  isSystem: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface CreateRoleDefinitionRequest {
  role: string
  label: string
  description?: string
  accountMappings?: AccountMapping[]
  billing?: Partial<BillingConfig>
}

export interface UpdateRoleDefinitionRequest {
  label?: string
  description?: string
  accountMappings?: AccountMapping[]
  billing?: Partial<BillingConfig>
  isActive?: boolean
}

// ============================================================================
// API Functions
// ============================================================================

export async function listRoleDefinitions(orgId: string): Promise<ApiResponse<RoleDefinition[]>> {
  return request<RoleDefinition[]>(`${API_BASE}/${orgId}/role-definitions`)
}

export async function getRoleDefinition(orgId: string, id: string): Promise<ApiResponse<RoleDefinition>> {
  return request<RoleDefinition>(`${API_BASE}/${orgId}/role-definitions/${id}`)
}

export async function createRoleDefinition(
  orgId: string,
  data: CreateRoleDefinitionRequest,
): Promise<ApiResponse<RoleDefinition>> {
  return request<RoleDefinition>(`${API_BASE}/${orgId}/role-definitions`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateRoleDefinition(
  orgId: string,
  id: string,
  data: UpdateRoleDefinitionRequest,
): Promise<ApiResponse<RoleDefinition>> {
  return request<RoleDefinition>(`${API_BASE}/${orgId}/role-definitions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

export async function deleteRoleDefinition(orgId: string, id: string): Promise<ApiResponse<void>> {
  return request<void>(`${API_BASE}/${orgId}/role-definitions/${id}`, {
    method: 'DELETE',
  })
}

export async function seedDefaultRoles(orgId: string): Promise<ApiResponse<{ status: string }>> {
  return request<{ status: string }>(`${API_BASE}/${orgId}/role-definitions/seed-defaults`, {
    method: 'POST',
  })
}
